import { useRef, useCallback, useEffect, useMemo } from 'react'
import { assign, createMachine } from 'xstate'
import { useMachine } from '@xstate/react'
import { useCreateSchedules } from 'hooks/queries/brandPlans/useCreateSchedules'
import { useGenerateThemePreview } from 'hooks/queries/brandPlans/useGenerateThemePreview'
import { useModals } from 'almanac-ui/Modal'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CheckBroken } from 'blaze-ui/icons'
import { ApiError } from 'utils/error'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import dayjs from 'dayjs'
import { MachineContext, MachineEvent, MachineState, EventId, StateId, CampaignRow, GeneratedTheme } from './Types'
import { BrandPlan, ContentStrategy, ContentStrategyType } from 'types'
import { ContentTypeCounts } from 'utils/brandPlans/contentCategories'
import { buildContentsFromCounts } from './contentTypeDefaultsUtils'
import { useContentPlanSchedules } from '../useContentPlanSchedules'

const createCampaignMachine = createMachine<MachineContext, MachineEvent, MachineState>({
  id: 'createCampaign',
  initial: StateId.SelectStrategy,
  states: {
    [StateId.SelectStrategy]: {
      on: {
        [EventId.Next]: [
          {
            target: StateId.ContentTypeDefaults,
            actions: 'seedCampaigns',
            cond: 'hasPostContentTypes',
          },
          {
            target: StateId.ReviewDetails,
            actions: 'seedCampaigns',
          },
        ],
        [EventId.Back]: {
          actions: 'close',
        },
      },
    },
    [StateId.ContentTypeDefaults]: {
      on: {
        [EventId.SetContentTypes]: {
          target: StateId.ReviewDetails,
          actions: assign({
            contentTypeCounts: (_, event) => event.counts,
          }),
        },
        [EventId.Back]: StateId.SelectStrategy,
      },
    },
    [StateId.ReviewDetails]: {
      entry: 'generateThemes',
      exit: assign({
        transient: (context) => {
          context.transient.abortController.abort()
          return {
            ...context.transient,
            abortController: new AbortController(),
          }
        },
      }),
      on: {
        [EventId.ThemeResolved]: {
          actions: assign({
            campaigns: (ctx, event) =>
              ctx.campaigns.map((row, i) => (i === event.index ? { ...row, theme: event.theme, loading: false } : row)),
          }),
        },
        [EventId.Back]: [
          {
            target: StateId.ContentTypeDefaults,
            cond: 'hasPostContentTypes',
          },
          {
            target: StateId.SelectStrategy,
          },
        ],
        [EventId.Create]: {
          target: StateId.Creating,
          actions: assign({
            campaigns: (_, event) => event.campaigns,
          }),
        },
        [EventId.Error]: {
          target: StateId.SelectStrategy,
          actions: 'showError',
        },
      },
    },
    [StateId.Creating]: {
      entry: 'createCampaigns',
      on: {
        [EventId.Error]: {
          target: StateId.ReviewDetails,
          actions: 'showError',
        },
      },
    },
  },
})

function computeStartDates(
  loadedSchedules: Array<{ content_strategy_type: string | null; end_date: string }>,
  strategyType: ContentStrategyType,
  strategyLength: number,
  campaignCount: number,
  timezone: string,
): Array<{ startDate: string; endDate: string; length: number }> {
  const matchingSchedules = loadedSchedules.filter((s) => s.content_strategy_type === strategyType)
  const lastSchedule = [...matchingSchedules].sort((a, b) => dayjs(b.end_date).diff(dayjs(a.end_date)))[0]

  const todayInTz = dayjs().tz(timezone)
  const tomorrow = todayInTz.add(1, 'day').startOf('day')
  const afterLastSchedule = lastSchedule ? dayjs(lastSchedule.end_date).add(1, 'day') : tomorrow
  const baseDate = afterLastSchedule.isAfter(tomorrow) ? afterLastSchedule : tomorrow

  return Array.from({ length: campaignCount }, (_, i) => {
    const startDate = baseDate.add(i * strategyLength, 'week')
    const endDate = startDate.add(strategyLength * 7 - 1, 'day')
    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      length: strategyLength,
    }
  })
}

interface Props {
  brandPlan: BrandPlan
  onSuccess?: () => void
}

export const useCreateCampaignMachine = ({ brandPlan, onSuccess }: Props) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { closeAllModals } = useModals()
  const sendRef = useRef<(event: MachineEvent) => void>(() => {})
  const prefilledThemesRef = useRef<Map<number, GeneratedTheme>>(new Map())

  const { mutateAsync: generateThemePreview } = useGenerateThemePreview({
    brandPlanId: brandPlan.id,
  })
  const { mutate: createSchedules } = useCreateSchedules(
    { brandPlanId: brandPlan.id },
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules'])
        closeAllModals()
        onSuccess?.()
        const count = data.length
        showToastMessage(
          `${count} new campaign${count === 1 ? '' : 's'} generated!`,
          'success',
          undefined,
          5,
          <CheckBroken size={22} />,
          'dark',
        )
      },
      onError: (error) => {
        if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
          const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
          showToastMessage(
            'Your content preferences require brand kit media, but none are uploaded.',
            'error',
            { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
            10,
          )
        } else {
          showErrorMessageToast('There was a problem creating your campaigns. Please try again.')
        }
        sendRef.current({ type: EventId.Error, error: error as Error })
      },
    },
  )

  const { data: schedulesData } = useContentPlanSchedules()
  const loadedSchedules = useMemo(
    () => schedulesData?.pages.flatMap((page) => page.data.schedules) ?? [],
    [schedulesData],
  )

  const buildScheduleParams = useCallback(
    (
      campaigns: CampaignRow[],
      strategyType: ContentStrategyType,
      config: ContentStrategy,
      contentTypeCounts: ContentTypeCounts | null,
    ) =>
      campaigns.map((campaign, index) => {
        const prefilled = prefilledThemesRef.current.get(index)
        const themeEdited = prefilled?.description !== campaign.theme.description

        const contentsOverride = contentTypeCounts ? buildContentsFromCounts(contentTypeCounts) : null

        return {
          start_date: campaign.startDate,
          content_strategy_type: strategyType,
          length: campaign.length,
          week_days: campaign.weekDays,
          daily_frequency: brandPlan.daily_frequency,
          days_before_review: brandPlan.days_before_review ?? 2,
          goal: brandPlan.goal ?? undefined,
          regenerate_themes: themeEdited,
          tactics: config.tactics,
          theme: campaign.theme.id,
          themes: [campaign.theme],
          sources: campaign.sources
            .filter((cs) => cs.roles.includes('source_material'))
            .map((cs) => ({ type: cs.source.type, id: cs.source.id })),
          media_files: campaign.sources
            .filter((cs) => cs.roles.includes('visual_asset') && cs.mediaFileId != null)
            .map((cs) => ({ id: cs.mediaFileId as number })),
          instagram_contents: contentsOverride?.instagram_contents ?? config.instagram_contents,
          crosspost_contents: contentsOverride?.crosspost_contents ?? config.crosspost_contents,
          seo_contents: contentsOverride?.seo_contents ?? config.seo_contents,
          email_contents: contentsOverride?.email_contents ?? config.email_contents,
          video_crosspost_contents: contentsOverride?.video_crosspost_contents ?? config.video_crosspost_contents,
          facebook_contents: brandPlan.facebook_contents,
          linkedin_contents: brandPlan.linkedin_contents,
          twitter_contents: brandPlan.twitter_contents,
          tiktok_contents: brandPlan.tiktok_contents,
          youtube_contents: brandPlan.youtube_contents,
          google_my_business_contents: brandPlan.google_my_business_contents,
        }
      }),
    [brandPlan],
  )

  const machine = useMachine<MachineContext, MachineEvent, MachineState>(createCampaignMachine, {
    context: {
      strategyType: null,
      strategyConfig: null,
      campaignCount: 1,
      campaigns: [],
      contentTypeCounts: null,
      transient: {
        abortController: new AbortController(),
      },
    },
    guards: {
      hasPostContentTypes: () => brandPlan.post_content_types,
    },
    actions: {
      close: () => closeAllModals(),
      showError: (_ctx, event) => {
        if ('error' in event && event.error) {
          showErrorMessageToast(event.error.message || 'Something went wrong. Please try again.')
        }
      },
      seedCampaigns: assign((_ctx, event) => {
        if (event.type !== EventId.Next) return {}
        const dates = computeStartDates(
          loadedSchedules,
          event.strategyType,
          event.strategyConfig.length,
          event.campaignCount,
          brandPlan.timezone,
        )
        return {
          strategyType: event.strategyType,
          strategyConfig: event.strategyConfig,
          campaignCount: event.campaignCount,
          contentTypeCounts: null,
          campaigns: dates.map((date, i) => ({
            theme: { id: `placeholder-${i}`, title: '', description: '', type: undefined },
            startDate: date.startDate,
            endDate: date.endDate,
            length: date.length,
            weekDays: brandPlan.week_days,
            sources: [],
            loading: true,
          })),
        }
      }),
      generateThemes: async (ctx) => {
        const abortController = ctx.transient.abortController
        try {
          if (!ctx.strategyType || !ctx.strategyConfig) return

          prefilledThemesRef.current.clear()
          const strategyType = ctx.strategyType
          const strategyConfig = ctx.strategyConfig
          const CONCURRENCY = 2
          const campaigns = ctx.campaigns

          const generatedThemes: Array<{ id: string; description: string }> = []

          for (let batchStart = 0; batchStart < campaigns.length; batchStart += CONCURRENCY) {
            if (abortController.signal.aborted) return

            const batch = campaigns.slice(batchStart, batchStart + CONCURRENCY)
            const existingThemes = [...generatedThemes]

            await Promise.all(
              batch.map((campaign, batchIndex) => {
                const index = batchStart + batchIndex
                return generateThemePreview({
                  content_strategy_type: strategyType,
                  start_date: campaign.startDate,
                  length: strategyConfig.length,
                  goal: brandPlan.goal ?? undefined,
                  existing_themes: existingThemes,
                }).then((result) => {
                  if (abortController.signal.aborted) return
                  generatedThemes.push({
                    id: result.theme.id,
                    description: result.theme.description,
                  })
                  prefilledThemesRef.current.set(index, result.theme)
                  sendRef.current({
                    type: EventId.ThemeResolved,
                    index,
                    theme: result.theme,
                  })
                })
              }),
            )
          }
        } catch (error) {
          if (abortController.signal.aborted) return
          sendRef.current({ type: EventId.Error, error: error as Error })
        }
      },
      createCampaigns: (ctx) => {
        if (!ctx.strategyType || !ctx.strategyConfig) return

        const params = buildScheduleParams(ctx.campaigns, ctx.strategyType, ctx.strategyConfig, ctx.contentTypeCounts)
        createSchedules(params as Parameters<typeof createSchedules>[0])
      },
    },
  })

  const [state, send] = machine
  sendRef.current = send

  const abortRef = useRef(state.context.transient.abortController)
  abortRef.current = state.context.transient.abortController

  useEffect(() => {
    return () => abortRef.current.abort()
  }, [])

  return machine
}
